import React, { useEffect, useMemo, useCallback, memo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { AppAsyncSelect, AppSelect } from '../../../../components/common/select';

const CompanySection = ({
  control,
  setValue,
  watch,
  formErrors,
  errors,
  handleSelectChange,
  handleTextInputChange,
  handleTextInputBlur,
  options,
  fetchContactOptions,
  lockCompany = false
}) => {
  // ✅ PERFORMANCE: Only watch specific fields needed, not all form data
  const company = useWatch({ control, name: 'company' });
  const contact = useWatch({ control, name: 'contact' });

  // The phone is normally copied from the selected company contact and kept read-only, but the
  // contact phone is optional on the Company form while it is required here. Without a number to
  // copy, the field has to be typeable, otherwise the lead can never be saved.
  const contactHasPhone = useMemo(() => {
    const selected = options.contactOptions?.find(c => String(c.value) === String(contact));
    return Boolean(selected?.contactNumber);
  }, [options.contactOptions, contact]);

  // useEffect(() => {
  //   alert(JSON.stringify(formData)); // 👈 Muncul saat pertama kali
  // }, []);

  // Auto-select first contact when contacts are loaded and no contact is selected
  useEffect(() => {
    if (
      company &&
      !contact &&
      options.contactOptions &&
      options.contactOptions.length > 0
    ) {
      // Auto-select the first contact
      const firstContact = options.contactOptions[0];
      if (firstContact && setValue) {
        setValue('contact', firstContact.value, { shouldValidate: false });
        if (firstContact.contactNumber) {
          setValue('contactPhoneNumber', firstContact.contactNumber, { shouldValidate: false });
        }
        if (firstContact.email) {
          setValue('email', firstContact.email, { shouldValidate: false });
        }
        // Also sync to parent
        if (handleSelectChange) {
          handleSelectChange('contact', firstContact.value);
          if (firstContact.contactNumber) {
            handleSelectChange('contactPhoneNumber', firstContact.contactNumber);
          }
          if (firstContact.email) {
            handleSelectChange('email', firstContact.email);
          }
        }
      }
    }
  }, [options.contactOptions, company, contact, setValue, handleSelectChange]);

  const handleContactChange = (name, value) => {
    handleSelectChange('contact', value);
    const selected = options.contactOptions?.find(c => String(c.value) === String(value));
    if (selected) {
      handleSelectChange('contactPhoneNumber', selected.contactNumber || '');
      handleSelectChange('email', selected.email || '');
    }
  };

  // Derive initialValue for AppAsyncSelect edit-mode pre-population from the already-fetched
  // companyOptions list. Falls back to a bare { value, label: '' } so at minimum the value is
  // held even before options load.
  const companyInitialValue = useMemo(() => {
    if (!options.companyOptions) return null;
    const found = options.companyOptions.find(o => String(o.value) === String(company));
    if (found) return { value: String(found.value), label: found.label || found.name || '' };
    if (company) return { value: String(company), label: '' };
    return null;
  }, [company, options.companyOptions]);

  // Auto-fill company sibling fields when a company is selected via AppAsyncSelect.
  // Receives the full option object, so no secondary find() is needed.
  const handleCompanyItemSelect = useCallback((item) => {
    if (!item) return;
    const companyWebsite = item.website || '';
    const companyPhoneNumber = item.contactNumber || '';
    const address = item.address || '';

    if (setValue) {
      setValue('companyWebsite', companyWebsite, { shouldValidate: false });
      setValue('companyPhoneNumber', companyPhoneNumber, { shouldValidate: false });
      setValue('address', address, { shouldValidate: false });
    }
    if (handleSelectChange) {
      handleSelectChange('companyWebsite', companyWebsite);
      handleSelectChange('companyPhoneNumber', companyPhoneNumber);
      handleSelectChange('address', address);
    }
    // Fetch contacts for the selected company
    if (fetchContactOptions && item[String('value') in item ? 'value' : 'id']) {
      fetchContactOptions(item.value ?? item.id);
    }
  }, [setValue, handleSelectChange, fetchContactOptions]);

  const formattedContactOptions = useMemo(() => (options.contactOptions?.map(opt => ({
    value: String(opt.value),
    label: opt.label,
    name: opt.name,
    contactNumber: opt.contactNumber,
    email: opt.email
  })) || []), [options.contactOptions]);

  return (
    <>
      <div className="inv-section-label">
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        Company
      </div>
      <div className="row">
        {/* Select Company — uses AppAsyncSelect for server-side search */}
        <div className="col-6">
          <Controller
            name="company"
            control={control}
            render={({ field }) => (
              <AppAsyncSelect
                name="company"
                label="Client"
                value={field.value || ''}
                onChange={handleSelectChange}
                loadUrl="/v2/crm/companies/options"
                placeholder="Search client..."
                required={true}
                error={formErrors?.company || errors.company?.message}
                disabled={lockCompany}
                valueField="value"
                labelField="label"
                searchField="label,name"
                initialValue={companyInitialValue}
                onItemSelect={handleCompanyItemSelect}
              />
            )}
          />
        </div>

        {/* Select Contact */}
        <div className="col-6">
          <Controller
            name="contact"
            control={control}
            render={({ field }) => (
              <AppSelect
                name="contact"
                label="Contact Name"
                value={field.value || ''}
                onChange={handleContactChange}
                options={formattedContactOptions}
                placeholder="Select a contact"
                required={true}
                error={formErrors?.contact || errors.contact?.message}
                disabled={!company}
                valueField="value"
                labelField="label"
                searchField="label,name"
                autoComplete="name"
              />
            )}
          />
        </div>

        {/* Contact Phone Number */}
        <div className="col-6 mb-3">
          <Controller
            name="contactPhoneNumber"
            control={control}
            render={({ field }) => (
              <>
                <label htmlFor="contactPhoneNumber" className="form-label">
                  Contact Phone Number <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  {...field}
                  className={`form-control ${formErrors?.contactPhoneNumber || errors.contactPhoneNumber ? 'is-invalid' : ''}`}
                  id="contactPhoneNumber"
                  onChange={(e) => {
                    field.onChange(e);
                    handleTextInputChange(e);
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    handleTextInputBlur(e);
                  }}
                  placeholder="Enter contact phone number"
                  disabled={!contact || contactHasPhone}
                  autoComplete="tel"
                />
                {(formErrors?.contactPhoneNumber || errors.contactPhoneNumber) && (
                  <div className="invalid-feedback">
                    {formErrors?.contactPhoneNumber || errors.contactPhoneNumber?.message}
                  </div>
                )}
              </>
            )}
          />
        </div>

        {/* Contact Email */}
        <div className="col-6">
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <>
                <label htmlFor="email" className="form-label">
                  Contact Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  {...field}
                  className={`form-control ${formErrors?.email || errors.email ? 'is-invalid' : ''}`}
                  id="email"
                  onChange={(e) => {
                    field.onChange(e);
                    handleTextInputChange(e);
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    handleTextInputBlur(e);
                  }}
                  placeholder="Enter email"
                  disabled
                  autoComplete="email"
                />
                {(formErrors?.email || errors.email) && (
                  <div className="invalid-feedback">
                    {formErrors?.email || errors.email?.message}
                  </div>
                )}
              </>
            )}
          />
        </div>

        {/* Company Address */}
        <div className="col-6 mb-3">
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <>
                <label htmlFor="address" className="form-label">
                  Address <span className="text-danger">*</span>
                </label>
                <textarea
                  {...field}
                  className={`form-control ${formErrors?.address || errors.address ? 'is-invalid' : ''}`}
                  id="address"
                  onChange={(e) => {
                    field.onChange(e);
                    handleTextInputChange(e);
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    handleTextInputBlur(e);
                  }}
                  placeholder="Enter address"
                  disabled
                  autoComplete="street-address"
                />
                {(formErrors?.address || errors.address) && (
                  <div className="invalid-feedback">
                    {formErrors?.address || errors.address?.message}
                  </div>
                )}
              </>
            )}
          />
        </div>

        {/* Company Phone Number */}
        <div className="col-6">
          <Controller
            name="companyPhoneNumber"
            control={control}
            render={({ field }) => (
              <>
                <label htmlFor="companyPhoneNumber" className="form-label">
                  Company Phone Number <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  {...field}
                  className={`form-control ${formErrors?.companyPhoneNumber || errors.companyPhoneNumber ? 'is-invalid' : ''}`}
                  id="companyPhoneNumber"
                  onChange={(e) => {
                    field.onChange(e);
                    handleTextInputChange(e);
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    handleTextInputBlur(e);
                  }}
                  placeholder="Enter company phone number"
                  disabled
                  autoComplete="tel"
                />
                {(formErrors?.companyPhoneNumber || errors.companyPhoneNumber) && (
                  <div className="invalid-feedback">
                    {formErrors?.companyPhoneNumber || errors.companyPhoneNumber?.message}
                  </div>
                )}
              </>
            )}
          />
        </div>

        {/* Sector */}
        <div className="col-6">
          <div className="mb-3">
            <label htmlFor="sector" className="form-label">Sector</label>
            <input
              type="text"
              className="form-control"
              id="sector"
              name="sector"
              value={(() => {
                const selectedCompany = options.companyOptions?.find(
                  c => String(c.value) === String(company)
                );
                return selectedCompany?.sectorName || '';
              })()}
              placeholder="Sector will be auto-filled"
              disabled
              autoComplete="organization"
            />
          </div>
        </div>

        {/* Company Website */}
        <div className="col-6">
          <Controller
            name="companyWebsite"
            control={control}
            render={({ field }) => (
              <>
                <label htmlFor="companyWebsite" className="form-label">
                  Company Website
                </label>
                <input
                  type="text"
                  {...field}
                  className={`form-control ${formErrors?.companyWebsite || errors.companyWebsite ? 'is-invalid' : ''}`}
                  id="companyWebsite"
                  onChange={(e) => {
                    field.onChange(e);
                    handleTextInputChange(e);
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    handleTextInputBlur(e);
                  }}
                  placeholder="Enter company website"
                  disabled
                  autoComplete="url"
                />
                {(formErrors?.companyWebsite || errors.companyWebsite) && (
                  <div className="invalid-feedback">
                    {formErrors?.companyWebsite || errors.companyWebsite?.message}
                  </div>
                )}
              </>
            )}
          />
        </div>
      </div>
    </>
  );
};

export default memo(CompanySection);
